import {
  ArrowRight,
  Globe,
  Inbox,
  KeyRound,
  Server,
  Sparkles,
} from "lucide-react";
import { Badge } from "~/components/ui/badge";
import { buttonVariants } from "~/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "~/components/ui/card";
import { FOCUS_RING, cn } from "~/lib/utils";

/** Same destination the public shell uses. Absolute app host is not required. */
const SIGN_IN_HREF = "/signin";

const EMBER_CTA = "focus-visible:ring-0 focus-visible:ring-offset-0";

/** The DNS records a domain must publish, in the order the panel shows them. */
const dnsRecords = [
  { label: "SPF", detail: "Authorises our senders" },
  { label: "DKIM", detail: "Two selectors, rotatable" },
  { label: "DMARC", detail: "p=quarantine" },
  { label: "MX", detail: "Inbound routed" },
];

const features = [
  {
    title: "Real mailboxes, not relays",
    description:
      "Every address is a full mailbox with its own SMTP and IMAP credentials. It sends, it receives, and it stores mail — so replies come back to the address that sent.",
    icon: Inbox,
  },
  {
    title: "Many domains, provisioned in bulk",
    description:
      "Claim a domain, publish four DNS records, and create mailboxes across it a hundred at a time. Outcomes are per mailbox, so a partial run tells you which two failed and retries only those.",
    icon: Globe,
  },
  {
    title: "Operations you can act on",
    description:
      "Provider and send jobs show as queue health. A failed mailbox create stays visible until you redrive it — the workspace does not invent a success.",
    icon: Server,
  },
  {
    title: "Credentials that rotate",
    description:
      "Rotate a mailbox password or a DKIM signing key without a gap. New credentials are verified before the old ones are revoked, so a failed rotation leaves the mailbox working.",
    icon: KeyRound,
  },
];

const steps = [
  {
    number: "01",
    title: "Claim a domain",
    description:
      "Add the domain your mail will come from and publish the DNS records we issue for it.",
  },
  {
    number: "02",
    title: "Create your mailboxes",
    description:
      "One at a time, pasted as a list, or imported from CSV — across as many domains as you hold.",
  },
  {
    number: "03",
    title: "Send and receive",
    description:
      "Use them from any client, or drive the whole estate through the API. Every operation the interface performs is a public endpoint.",
  },
];

export function InboxFuryMarketingScreen() {
  return (
    <div
      className="min-h-screen overflow-hidden bg-background text-foreground"
      data-testid="inboxfury-marketing-page"
    >
      <header className="relative z-20 border-b border-border/70 bg-background/95 backdrop-blur">
        <div className="mx-auto flex min-h-16 max-w-7xl flex-wrap items-center justify-between gap-x-3 gap-y-2 px-5 py-2 sm:px-8">
          <a
            href="/"
            className={cn(
              "inline-flex items-center gap-2.5 rounded-sm font-semibold tracking-tight",
              FOCUS_RING,
            )}
            aria-label="InboxFury home"
          >
            <span className="flex size-8 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm">
              <Inbox className="size-4" aria-hidden="true" />
            </span>
            <span className="text-lg">InboxFury</span>
          </a>

          <nav
            aria-label="Primary navigation"
            className="hidden items-center gap-7 text-sm text-foreground-muted md:flex"
          >
            <a className="hover:text-foreground" href="#platform">
              Platform
            </a>
            <a className="hover:text-foreground" href="#how-it-works">
              How it works
            </a>
          </nav>

          <div className="flex min-w-0 items-center gap-2">
            <a
              href={SIGN_IN_HREF}
              className={cn(
                buttonVariants({ variant: "ghost", size: "sm" }),
                "hidden sm:inline-flex",
                EMBER_CTA,
                FOCUS_RING,
              )}
            >
              Sign in
            </a>
            <a
              href={SIGN_IN_HREF}
              className={cn(buttonVariants({ size: "sm" }), EMBER_CTA, FOCUS_RING)}
              data-testid="inboxfury-marketing-header-cta"
            >
              <span className="sm:hidden">Open workspace</span>
              <span className="hidden sm:inline">Open your workspace</span>
            </a>
          </div>
        </div>
      </header>

      <main>
        <section
          className="relative border-b border-border/70"
          data-testid="inboxfury-marketing-hero"
        >
          <div
            className="pointer-events-none absolute inset-0 opacity-70"
            aria-hidden="true"
            style={{
              background:
                "radial-gradient(circle at 70% 20%, hsl(var(--color-primary) / 0.16), transparent 34%), radial-gradient(circle at 12% 65%, hsl(var(--color-info) / 0.10), transparent 30%)",
            }}
          />
          <div className="relative mx-auto grid max-w-7xl gap-14 px-5 py-20 sm:px-8 sm:py-28 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:py-32">
            <div className="max-w-3xl">
              <Badge
                variant="outline"
                className="mb-6 gap-1.5 border-foreground text-foreground"
              >
                <Sparkles className="size-3.5" aria-hidden="true" />
                Mailbox infrastructure, under your own domains
              </Badge>
              <h1 className="text-balance text-4xl font-semibold tracking-tight sm:text-6xl lg:text-7xl">
                Hundreds of real mailboxes. Across all your domains.
              </h1>
              <p className="mt-6 max-w-2xl text-pretty text-lg leading-8 text-foreground-muted sm:text-xl">
                InboxFury provisions and runs email infrastructure — domains
                verified, mailboxes created in bulk, and an API under all of it.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <a
                  href={SIGN_IN_HREF}
                  className={cn(buttonVariants({ size: "lg" }), EMBER_CTA, FOCUS_RING)}
                  data-testid="inboxfury-marketing-primary-cta"
                >
                  Sign in to your workspace
                  <ArrowRight className="size-4" aria-hidden="true" />
                </a>
                <a
                  href="mailto:sales@inboxfury.com?subject=InboxFury%20workspace"
                  className={cn(
                    buttonVariants({ variant: "outline", size: "lg" }),
                    EMBER_CTA,
                    FOCUS_RING,
                  )}
                >
                  Request a workspace
                </a>
              </div>
              <p className="mt-4 text-sm text-foreground-muted">
                Your domains, your mailboxes, your data. Export or delete both at
                any time.
              </p>
            </div>

            <figure
              className="relative mx-auto w-full max-w-xl"
              aria-label="Schematic of DNS records issued for a claimed domain"
            >
              <div
                className="absolute -inset-6 rounded-[2rem] bg-primary/10 blur-3xl"
                aria-hidden="true"
              />
              <Card className="relative border-border-strong/70 bg-background/95 shadow-lg">
                <CardHeader className="border-b">
                  <p className="font-mono text-xs font-semibold uppercase tracking-[0.14em] text-foreground-muted">
                    Schematic
                  </p>
                  <p className="mt-2 text-base font-semibold tracking-tight">
                    DNS records issued for a claimed domain
                  </p>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-3">
                    {dnsRecords.map(({ label, detail }) => (
                      <div
                        key={label}
                        className="flex items-center gap-4 rounded-lg border bg-surface-1 p-4"
                      >
                        <dt className="flex size-10 shrink-0 items-center justify-center rounded-md border bg-background font-mono text-xs font-semibold text-primary">
                          {label}
                        </dt>
                        <dd className="min-w-0 flex-1 font-medium">{detail}</dd>
                      </div>
                    ))}
                  </dl>
                </CardContent>
              </Card>
            </figure>
          </div>
        </section>

        <section id="platform" className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
          <div className="max-w-2xl">
            <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">
              The whole estate
            </p>
            <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
              Built for ninety mailboxes, not nine.
            </h2>
            <p className="mt-4 text-lg leading-8 text-foreground-muted">
              Everything here assumes you are running mail at a scale where
              doing it by hand stopped working.
            </p>
          </div>

          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {features.map(({ title, description, icon: Icon }) => (
              <Card key={title} className="bg-surface-1/60 shadow-none">
                <CardHeader>
                  <span className="mb-2 flex size-10 items-center justify-center rounded-md bg-primary/10 text-primary">
                    <Icon className="size-5" aria-hidden="true" />
                  </span>
                  <h3 className="font-semibold leading-none">{title}</h3>
                  <CardDescription className="text-base leading-7">
                    {description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </section>

        <section
          id="how-it-works"
          className="border-y border-border/70 bg-surface-1"
        >
          <div className="mx-auto max-w-7xl px-5 py-20 sm:px-8">
            <div className="max-w-2xl">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-primary">
                From a bare domain to sending mail
              </p>
              <h2 className="mt-3 text-3xl font-semibold tracking-tight sm:text-4xl">
                Three steps, and none of them involve a support ticket.
              </h2>
            </div>
            <ol className="mt-10 grid gap-8 lg:grid-cols-3">
              {steps.map(({ number, title, description }) => (
                <li key={number} className="border-t border-border-strong pt-6">
                  <span className="font-mono text-sm text-primary">
                    {number}
                  </span>
                  <h3 className="mt-4 text-xl font-semibold">{title}</h3>
                  <p className="mt-3 leading-7 text-foreground-muted">
                    {description}
                  </p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="mx-auto max-w-7xl px-5 py-20 sm:px-8 sm:py-24">
          <div className="rounded-2xl bg-foreground px-6 py-12 text-center text-background shadow-lg sm:px-12">
            <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
              Bring whatever sends the mail.
            </h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg leading-8 text-background/70">
              InboxFury runs the mailboxes. What you point at them is your
              business — the API is the same either way.
            </p>
            <a
              href="mailto:sales@inboxfury.com?subject=InboxFury%20workspace"
              className={cn(
                buttonVariants({ size: "lg" }),
                "mt-8 bg-background text-foreground hover:bg-background/90",
                EMBER_CTA,
                FOCUS_RING,
              )}
            >
              Request a workspace
              <ArrowRight className="size-4" aria-hidden="true" />
            </a>
          </div>
        </section>
      </main>

      <footer className="border-t border-border bg-surface-1">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 py-8 text-sm text-foreground-muted sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div className="flex items-center gap-2 font-medium text-foreground">
            <Inbox className="size-4 text-primary" aria-hidden="true" />
            InboxFury
          </div>
          <nav aria-label="Footer" className="flex flex-wrap gap-5">
            <a className={cn("hover:text-foreground", FOCUS_RING)} href={SIGN_IN_HREF}>
              Sign in
            </a>
          </nav>
        </div>
      </footer>
    </div>
  );
}
